import { SaveOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Modal, Popconfirm, Row, message } from 'antd';
import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DescriptionForm from './DescriptionForm/DescriptionForm';
import TradeForm from './TradeForm/TradeForm';

import { finalizeCreateAuction, patchAuction, startCreateAuction } from 'apis/auction';
import { AuctionFormField, setAuctionFormState, setErrors, updateSyncedAuction } from 'features/auctionFormSlice';
import useAppSelector from 'hooks/useAppSelector';
import { AuctionPatch } from 'models/auction/Auction';

import { isEqual } from 'underscore';

import ReactRouterPrompt from 'react-router-prompt';
import styles from './AuctionForm.module.css';

interface AuctionFormProps {
  closeAuctionForm: (createdAuctionId: string) => void;
}

function AuctionForm({ closeAuctionForm }: AuctionFormProps) {
  const dispatch = useDispatch();
  const {
    id,
    syncedAuction,
    title,
    condition,
    description,
    images,
    duration,
    shippingMethods,
    payment,
    returnPolicy,
    invitationCodeRequired,
  } = useAppSelector((state) => state.auctionForm);

  const [isSyncLoading, setIsSyncLoading] = useState<boolean>(false);
  const [isFinalizeLoading, setIsFinalizeLoading] = useState<boolean>(false);
  const isLoading = isSyncLoading || isFinalizeLoading;

  const [isOpenPopconfirm, setIsOpenPopconfirm] = useState(false);

  const isSyncNeeded =
    title !== syncedAuction?.title ||
    description !== syncedAuction.description ||
    duration !== syncedAuction.duration ||
    !isEqual(
      images.map(({ id }) => id),
      syncedAuction.images.map(({ id }) => id)
    ) ||
    !isEqual(shippingMethods, syncedAuction.shipping_methods) ||
    payment !== syncedAuction.payment ||
    returnPolicy !== syncedAuction.return_policy ||
    invitationCodeRequired !== syncedAuction.has_invitation_code;

  const validateAuctionForm = () => {
    const invalidFields: AuctionFormField[] = [];

    // Check Empty String
    if (title === '') invalidFields.push('title');
    if (description === '') invalidFields.push('description');
    if (payment === '') invalidFields.push('payment');
    if (returnPolicy === '') invalidFields.push('returnPolicy');

    // Check Empty Array
    if (images.length === 0) invalidFields.push('images');
    if (shippingMethods.length === 0) invalidFields.push('shippingMethods');

    // Update state
    if (invalidFields.length !== 0) {
      dispatch(setErrors(invalidFields));
      message.error('올바르지 않은 정보가 있습니다. 입력 내용을 확인해주세요.');
      return false;
    }

    return true;
  };

  const finalizeAuction = () => {
    if (isFinalizeLoading) return;

    setIsFinalizeLoading(true);

    finalizeCreateAuction(id)
      .then(({ invitation_code }) => {
        let msg = '경매가 생성되었습니다.';
        let invitationCodeMsg = null;
        // modal
        if (invitation_code) {
          invitationCodeMsg = '제목 아래 초대 코드를 복사해 공유해주세요!';
        }
        message.success(
          <>
            <span>{msg}</span>
            {invitationCodeMsg && <div>{invitationCodeMsg}</div>}
          </>,
          5
        );

        closeAuctionForm(id);
      })
      .catch((errMsg) => {
        if (errMsg !== '') message.error(errMsg);
      })
      .finally(() => setIsFinalizeLoading(false));
  };

  const syncAuction = (auctionId: string, option?: { finalizeAfterSync?: boolean; isAutoSave?: boolean }) => {
    if (isSyncLoading) return;

    setIsSyncLoading(true);

    const auctionPatch: AuctionPatch = {
      title,
      description,
      duration,
      condition,
      image_ids: images.map(({ id }) => id),
      shipping_methods: shippingMethods,
      payment,
      return_policy: returnPolicy,
      invitation_code_required: invitationCodeRequired,
    };

    patchAuction(auctionId, auctionPatch)
      .then((auction) => {
        dispatch(updateSyncedAuction(auction));
        if (option?.finalizeAfterSync) finalizeAuction();
        else if (!option?.isAutoSave) message.success('저장되었습니다.');
      })
      .catch((errMsg) => {
        if (errMsg) message.error(errMsg);
      })
      .finally(() => setIsSyncLoading(false));
  };

  const checkIdThenSyncAuction = (auctionId: string, option: { isSyncNeeded: boolean; isAutoSave?: boolean }) => {
    if (option.isSyncNeeded) {
      if (!auctionId) {
        startCreateAuction()
          .then((auction) => {
            dispatch(setAuctionFormState({ id: auction.id }));
            syncAuction(auction.id, { isAutoSave: option.isAutoSave });
          })
          .catch((errMsg) => {
            if (errMsg !== '') message.error(errMsg);
          });
      } else syncAuction(auctionId, { isAutoSave: option.isAutoSave });
    } else if (!option.isAutoSave) {
      message.success('저장되었습니다.');
    }
  };

  const onClickSubmitButton = () => {
    if (isSyncNeeded) syncAuction(id, { finalizeAfterSync: true });
    else finalizeAuction();
  };

  const preventLeave = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    (() => {
      window.addEventListener('beforeunload', preventLeave);
    })();

    return () => {
      window.removeEventListener('beforeunload', preventLeave);
    };
  }, []);

  return (
    <>
      <Card className={styles.container}>
        <DescriptionForm />
        <Divider />
        <TradeForm />
        <Row justify="end" gutter={12}>
          <Col>
            <Button
              loading={isSyncLoading}
              disabled={isSyncLoading}
              icon={<SaveOutlined />}
              onClick={() => checkIdThenSyncAuction(id, { isSyncNeeded })}
            >
              임시저장
            </Button>
          </Col>
          <Col>
            <Popconfirm
              overlayClassName={styles.confirm}
              open={isOpenPopconfirm}
              placement="topLeft"
              title="정말 등록하시겠습니까?"
              description="경매 낙찰 후에는 (사망, 전쟁, 천재지변으로 인한 상품의 손상 등 부득이한 경우를 제외하고) 판매를 거부할 수 없습니다."
              onConfirm={onClickSubmitButton}
              onCancel={() => setIsOpenPopconfirm(false)}
              okText="등록"
              cancelText="취소"
              okButtonProps={{ loading: isLoading, disabled: isLoading }}
            >
              <Button
                type="primary"
                loading={isLoading}
                disabled={isLoading}
                icon={<SendOutlined />}
                onClick={() => {
                  if (validateAuctionForm()) setIsOpenPopconfirm(true);
                }}
              >
                등록
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <ReactRouterPrompt
        when={
          (title !== '' && title !== syncedAuction?.title) ||
          (description !== '' && description !== syncedAuction?.description) ||
          (shippingMethods.length !== 0 && !isEqual(shippingMethods, syncedAuction?.shipping_methods)) ||
          (payment !== '' && payment !== syncedAuction?.payment) ||
          (returnPolicy !== '' && returnPolicy !== syncedAuction?.return_policy)
        }
      >
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <Modal
              open={true}
              title="페이지를 벗어나시겠습니까?"
              onOk={onConfirm}
              onCancel={onCancel}
              okText="이동"
              cancelText="취소"
            >
              변경사항이 저장되지 않을 수 있습니다.
            </Modal>
          )
        }
      </ReactRouterPrompt>
    </>
  );
}

export default memo(AuctionForm);
